import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit';
import { RootState } from "../../../../../app/store";
import {
    ToolSettingsResponse,
    UpdateBotToolSettingsRequest,
    UpdateRequestsConfigurationRequest,
    UpdateBookingConfigRequest,
    BookingType,
    RequestMode,
    UpdateConditionMultipliersRequest,
    UpdateRetailPriceRuleRequest,
    UpdateServiceHistoryMultipliersRequest,
    Currency,
    FieldOptionDTO,
    UpdateModifierRulesRequest,
    UpdateRuleSectionRequest,
    UpdateConditionGroupRequest,
    UpdateConditionRequest,
    ActionType,
    ConditionOperator,
    ConditionField,
    SubtractionType,
    FinanceProvider,
    IVendiProviderConfigRequest,
    ValuationPriceType,
    ValuationProvider
} from "../../../../../api/generated";
import { RequestStatus, botApi, ValuationRulesConfigApi } from "../../../../../api/apiConfiguration";
import { selectExceptionally } from '../../../../../utils/selectExceptionally';

export interface UpdateConditionRequestUI extends Omit<UpdateConditionRequest, 'id' | 'comparedValues'> {
    id?: number;
    comparedValues?: string[];
}

export interface UpdateConditionGroupRequestUI extends Omit<UpdateConditionGroupRequest, 'id' | 'conditions'> {
    id?: number;
    conditions?: UpdateConditionRequestUI[];
}

export interface UpdateRuleSectionRequestUI extends Omit<UpdateRuleSectionRequest, 'id' | 'conditionGroups'> {
    id?: number;
    conditionGroups?: UpdateConditionGroupRequestUI[];
}

export interface UpdateModifierRulesRequestUI extends Omit<UpdateModifierRulesRequest, 'ruleSections'> {
    ruleSections?: UpdateRuleSectionRequestUI[];
}

export type UpdateRetailPriceRuleRequestPartial = Partial<Omit<UpdateRetailPriceRuleRequest, "min">>;

export type UpdateToolSettingsRequestUI = Omit<UpdateBotToolSettingsRequest, 'modifierRules' | 'retailPriceRules'> & {
    modifierRules: UpdateModifierRulesRequestUI;
    retailPriceRules: Array<UpdateRetailPriceRuleRequestPartial>;
};

export type UpdateToolSettingsRequestPartial = Partial<UpdateToolSettingsRequestUI>;

export const fetchToolSettingsById = createAsyncThunk(
    'toolSettings/fetchById',
    async (botPublicId: string) => {
        return await botApi.getBotToolSettings({ botPublicId });
    }
);

export const fetchFieldOptions = createAsyncThunk(
    'toolSettings/fetchFieldOptions',
    async () => {
        return await ValuationRulesConfigApi.getFieldOptions();
    }
);

export const MAX_NUMBER = 1000000000;

function sortRetailPriceRules(rules: UpdateRetailPriceRuleRequest[]): UpdateRetailPriceRuleRequest[] {
    if (rules.length === 0) return [];
    const minMap = new Map<number, UpdateRetailPriceRuleRequest>();
    const maxSet = new Set<number>();

    for (const rule of rules) {
        minMap.set(rule.min, rule);
        maxSet.add(rule.max);
    }

    const start = rules.find(rule => !maxSet.has(rule.min));
    if (!start) throw new Error("Cannot find valid start element");

    const sorted: UpdateRetailPriceRuleRequest[] = [];
    let current = start;
    while (current) {
        sorted.push(current);
        current = minMap.get(current.max)!;
    }

    return sorted;
}

function mapRetailPriceRules(rules: UpdateRetailPriceRuleRequestPartial[]): UpdateRetailPriceRuleRequest[] {
    if (rules.length === 0) return [];
    const mappedRules: UpdateRetailPriceRuleRequest[] = [];

    for (let i = 0; i < rules.length; i++) {
        let min = 0;
        if (i !== 0) {
            const previousMax = rules[i - 1].max;
            if (previousMax === undefined) {
                throw new Error(`Rule at index ${i - 1} has undefined max value`);
            }
            min = previousMax;
        }

        const currentRule = rules[i];
        if (currentRule.max === undefined) {
            throw new Error(`Rule at index ${i} has undefined max value`);
        }

        mappedRules.push({
            ...currentRule,
            min: min
        } as UpdateRetailPriceRuleRequest);
    }

    return mappedRules;
}

function mapToolSettingsResponseToRequest(
    payload: ToolSettingsResponse,
    currentBotPublicId: string
): UpdateToolSettingsRequestUI {
    return {
        botPublicId: currentBotPublicId,
        toolSettingsId: payload.toolSettingsId,
        requestsConfiguration: {
            id: payload.requestsConfiguration.id || 0,
            callbackRequestsEnabled: payload.requestsConfiguration.callbackRequestsEnabled,
            testDriveRequestsEnabled: payload.requestsConfiguration.testDriveRequestsEnabled,
            callbackMode: payload.requestsConfiguration.callbackMode || RequestMode.Booking,
            testDriveMode: payload.requestsConfiguration.testDriveMode || RequestMode.Booking
        },
        bookingConfigs: payload.bookingConfigs.map(config => ({
            id: config.id,
            bookingType: config.bookingType,
            bookingStepSeconds: config.bookingStepSeconds,
            timeToCloseSeconds: config.timeToCloseSeconds,
            minimumNoticeSeconds: config.minimumNoticeSeconds
        })),
        vehicleSearchConfigId: payload.vehicleSearchConfig.id || 0,
        searchNearestByPostcodeEnabled: payload.vehicleSearchConfig.searchNearestByPostcodeEnabled,
        financeConfigurationId: payload.financeConfigurationId || 0,
        financeIddUrl: payload.financeIddUrl,
        financeInterestRate: payload.financeInterestRate,
        financeProvider: payload.financeProvider || FinanceProvider.Illustration,
        ivendiProviderConfig: payload.ivendiProviderConfig ? {
            id: payload.ivendiProviderConfig.id,
            apiKey: payload.ivendiProviderConfig.apiKey,
            clientId: payload.ivendiProviderConfig.clientId,
            clientSecret: payload.ivendiProviderConfig.clientSecret,
            locationQuoteeIds: payload.ivendiProviderConfig.locationQuoteeIds?.map(loc => ({
                locationPublicId: loc.locationPublicId,
                quoteeId: loc.quoteeId
            })) || []
        } : undefined,
        pcpConfigurationId: payload.pcpConfigurationId || 0,
        isPcpEnabled: payload.isPcpEnabled,
        pcpAPR: payload.pcpAPR,
        pcpResidualPercentage: payload.pcpResidualPercentage,
        pcpDealerContribution: payload.pcpDealerContribution,
        valuationConfigurationId: payload.valuationConfigurationId,
        valuationPriceType: payload.valuationPriceType || ValuationPriceType.Retail,
        valuationProvider: payload.valuationProvider || ValuationProvider.Autotrader,
        valuationServiceGroupSoftwareHash: payload.valuationServiceGroupSoftwareHash,
        retailPriceRules: sortRetailPriceRules(payload.retailPriceRules.length > 0 ? payload.retailPriceRules.map(rule => ({
            id: rule.id,
            min: rule.min,
            max: rule.max,
            subtraction: {
                id: rule.subtraction.id,
                subtractionValue: rule.subtraction.subtractionValue,
                subtractionType: rule.subtraction.subtractionType
            }
        })) : [{
            min: 0,
            max: MAX_NUMBER,
            subtraction: {
                subtractionType: SubtractionType.Percentage,
                subtractionValue: 20
            }
        }]),
        conditionMultipliers: payload.conditionMultipliers as UpdateConditionMultipliersRequest,
        serviceHistoryMultipliers: payload.serviceHistoryMultipliers as UpdateServiceHistoryMultipliersRequest,
        minDeduction: payload.minDeduction,
        maxDeduction: payload.maxDeduction,
        modifierRules: {
            id: payload.modifierRules.id || 0,
            isActive: payload.modifierRules.isActive || false,
            ruleSections: payload.modifierRules.ruleSections
                ? Array.from(payload.modifierRules.ruleSections).map(section => ({
                    id: section.id,
                    isAllMatch: section.isAllMatch || false,
                    valuationAction: {
                        id: section.valuationAction?.id,
                        actionType: section.valuationAction?.actionType || ActionType.Exclude,
                        priceModifier: section.valuationAction?.priceModifier ? {
                            id: section.valuationAction.priceModifier.id,
                            subtractionValue: section.valuationAction.priceModifier.subtractionValue,
                            subtractionType: section.valuationAction.priceModifier.subtractionType
                        } : undefined
                    },
                    conditionGroups: section.conditionGroups
                        ? Array.from(section.conditionGroups).map(group => ({
                            id: group.id,
                            isAllMatch: group.isAllMatch || false,
                            conditions: group.conditions
                                ? Array.from(group.conditions).map(condition => ({
                                    id: condition.id,
                                    operator: condition.operator || ConditionOperator.Equals,
                                    conditionField: condition.conditionField || ConditionField.Price,
                                    comparedValues: condition.comparedValues || []
                                }))
                                : []
                        }))
                        : []
                }))
                : []
        },
        reservationConfigurationId: payload.reservationConfigurationId,
        stripePublicKey: payload.stripePublicKey,
        stripeSecretKey: payload.stripeSecretKey,
        currency: payload.currency || Currency.Gbp,
        price: payload.price
    };
}

export const updateToolSettings = createAsyncThunk<ToolSettingsResponse, string, {
    state: RootState
}>(
    "toolSettings/update",
    (botPublicId, { getState }) => {
        const state = getState();
        const updateRequestUI = state.toolSettings.updateRequest;

        const updateRequest: any = {
            ...updateRequestUI,
            botPublicId,
            retailPriceRules: mapRetailPriceRules(updateRequestUI.retailPriceRules as UpdateRetailPriceRuleRequestPartial[]),
            modifierRules: {
                ...updateRequestUI.modifierRules,
                ruleSections: updateRequestUI.modifierRules.ruleSections 
                    ? updateRequestUI.modifierRules.ruleSections.map((section: UpdateRuleSectionRequestUI) => ({
                        ...section,
                        conditionGroups: section.conditionGroups 
                            ? section.conditionGroups.map((group: UpdateConditionGroupRequestUI) => ({
                                ...group,
                                conditions: group.conditions ? group.conditions : []
                            }))
                            : []
                    }))
                    : []
            },
            ivendiProviderConfig: updateRequestUI.ivendiProviderConfig ? {
                ...updateRequestUI.ivendiProviderConfig,
                locationQuoteeIds: updateRequestUI.ivendiProviderConfig.locationQuoteeIds
            } : undefined
        };

        return botApi.updateBotToolSettings({
            updateBotToolSettingsRequest: updateRequest as UpdateBotToolSettingsRequest
        });
    }
);

export interface ToolSettingsState {
    toolSettings?: ToolSettingsResponse;
    updateRequest: UpdateToolSettingsRequestUI;
    toolSettingsRequestStatus: RequestStatus;
    updateSettingsStatus: RequestStatus;
    hasUnsavedChanges: boolean;
    fieldOptions: FieldOptionDTO[];
    fieldOptionsStatus: RequestStatus;
}

const initialState: ToolSettingsState = {
    toolSettings: undefined,
    updateRequest: {
        botPublicId: "",
        toolSettingsId: 0,
        requestsConfiguration: {
            id: 0,
            callbackRequestsEnabled: false,
            testDriveRequestsEnabled: false,
            callbackMode: RequestMode.Booking,
            testDriveMode: RequestMode.Booking
        },
        bookingConfigs: [],
        vehicleSearchConfigId: 0,
        searchNearestByPostcodeEnabled: false,
        financeConfigurationId: 0,
        financeIddUrl: undefined,
        financeInterestRate: undefined,
        financeProvider: FinanceProvider.Illustration,
        ivendiProviderConfig: undefined,
        pcpConfigurationId: 0,
        isPcpEnabled: false,
        pcpAPR: undefined,
        pcpResidualPercentage: undefined,
        pcpDealerContribution: undefined,
        valuationConfigurationId: 0,
        valuationPriceType: ValuationPriceType.Retail,
        valuationProvider: ValuationProvider.Autotrader,
        valuationServiceGroupSoftwareHash: undefined,
        retailPriceRules: [],
        conditionMultipliers: {
            excellentSubtraction: { subtractionValue: 0, subtractionType: "PERCENTAGE" },
            goodSubtraction: { subtractionValue: 0, subtractionType: "PERCENTAGE" },
            averageSubtraction: { subtractionValue: 0, subtractionType: "PERCENTAGE" },
            belowAverageSubtraction: { subtractionValue: 0, subtractionType: "PERCENTAGE" },
            poorSubtraction: { subtractionValue: 0, subtractionType: "PERCENTAGE" }
        },
        serviceHistoryMultipliers: {
            fullHistorySubtraction: { subtractionValue: 0, subtractionType: "PERCENTAGE" },
            partHistorySubtraction: { subtractionValue: 0, subtractionType: "PERCENTAGE" },
            noHistorySubtraction: { subtractionValue: 0, subtractionType: "PERCENTAGE" }
        },
        minDeduction: 0,
        maxDeduction: 0,
        modifierRules: {
            id: 0,
            isActive: true,
            ruleSections: []
        },
        reservationConfigurationId: 0,
        stripePublicKey: undefined,
        stripeSecretKey: undefined,
        currency: undefined,
        price: undefined
    },
    toolSettingsRequestStatus: RequestStatus.NONE,
    updateSettingsStatus: RequestStatus.NONE,
    hasUnsavedChanges: false,
    fieldOptions: [],
    fieldOptionsStatus: RequestStatus.NONE
};

export const toolSettingsSlice = createSlice({
    name: 'toolSettings',
    initialState,
    reducers: {
        updateSettings: (state, action: PayloadAction<UpdateToolSettingsRequestPartial>) => {
            state.updateRequest = {
                ...state.updateRequest,
                ...action.payload
            };
            state.hasUnsavedChanges = true;
        },
        addNewRetailPriceRule: (state, { payload }: PayloadAction<number>) => {
            const retailPriceRules = selectExceptionally(state.updateRequest).retailPriceRules;
            if (retailPriceRules) {
                retailPriceRules.splice(payload, 0, {
                    max: undefined,
                    subtraction: {
                        subtractionType: SubtractionType.Percentage,
                        subtractionValue: -1
                    }
                });

                state.hasUnsavedChanges = true;
            }
        },
        updateRetailPriceRule: (state, { payload }: PayloadAction<UpdateRetailPriceRuleRequestPartial & { ruleIndex: number }>) => {
            const retailPriceRules = selectExceptionally(state.updateRequest).retailPriceRules;
            if (retailPriceRules) {
                const { ruleIndex, ...ruleData } = payload;
                retailPriceRules.splice(ruleIndex, 1, ruleData);
                state.hasUnsavedChanges = true;
            }
        },
        deleteRetailPriceRule: (state, { payload }: PayloadAction<number>) => {
            const retailPriceRules = selectExceptionally(state.updateRequest).retailPriceRules;
            if (retailPriceRules) {
                if (payload === retailPriceRules.length - 1) {
                    const previousRuleIndex = payload - 1;
                    const previousRule = retailPriceRules[previousRuleIndex];
                    if (previousRule) {
                        previousRule.max = MAX_NUMBER;
                        retailPriceRules.splice(previousRuleIndex, 1, previousRule);
                    }
                }
                retailPriceRules.splice(payload, 1);
                state.hasUnsavedChanges = true;
            }
        },
        updateModifierRulesBasic: (state, action: PayloadAction<{ isActive?: boolean }>) => {
            if (state.updateRequest.modifierRules) {
                if (action.payload.isActive !== undefined) {
                    state.updateRequest.modifierRules.isActive = action.payload.isActive;
                }
                state.hasUnsavedChanges = true;
            }
        },
        addRuleSection: (state) => {
            const newSection: UpdateRuleSectionRequestUI = {
                id: undefined,
                isAllMatch: false,
                valuationAction: {
                    actionType: ActionType.Exclude,
                    priceModifier: undefined
                },
                conditionGroups: []
            };
            if (!state.updateRequest.modifierRules.ruleSections) {
                state.updateRequest.modifierRules.ruleSections = [];
            }
            state.updateRequest.modifierRules.ruleSections.push(newSection);
            state.hasUnsavedChanges = true;
        },
        updateRuleSection: (state, action: PayloadAction<{ sectionIndex: number; section: UpdateRuleSectionRequestUI }>) => {
            if (state.updateRequest.modifierRules?.ruleSections) {
                if (state.updateRequest.modifierRules.ruleSections[action.payload.sectionIndex]) {
                    state.updateRequest.modifierRules.ruleSections[action.payload.sectionIndex] = action.payload.section as any;
                    state.hasUnsavedChanges = true;
                }
            }
        },
        deleteRuleSection: (state, action: PayloadAction<number>) => {
            if (state.updateRequest.modifierRules?.ruleSections) {
                state.updateRequest.modifierRules.ruleSections.splice(action.payload, 1);
                state.hasUnsavedChanges = true;
            }
        },
        addConditionGroup: (state, action: PayloadAction<number>) => {
            if (state.updateRequest.modifierRules?.ruleSections) {
                const section = state.updateRequest.modifierRules.ruleSections[action.payload];
                if (section) {
                    const newGroup: UpdateConditionGroupRequestUI = {
                        id: undefined,
                        isAllMatch: false,
                        conditions: []
                    };
                    if (!section.conditionGroups) {
                        section.conditionGroups = [];
                    }
                    section.conditionGroups.push(newGroup);
                    state.hasUnsavedChanges = true;
                }
            }
        },
        updateConditionGroup: (state, action: PayloadAction<{ sectionIndex: number; groupIndex: number; group: UpdateConditionGroupRequestUI }>) => {
            if (state.updateRequest.modifierRules?.ruleSections) {
                const section = state.updateRequest.modifierRules.ruleSections[action.payload.sectionIndex];
                if (section?.conditionGroups && section.conditionGroups[action.payload.groupIndex]) {
                    section.conditionGroups[action.payload.groupIndex] = action.payload.group;
                    state.hasUnsavedChanges = true;
                }
            }
        },
        deleteConditionGroup: (state, action: PayloadAction<{ sectionIndex: number; groupIndex: number }>) => {
            if (state.updateRequest.modifierRules?.ruleSections) {
                const section = state.updateRequest.modifierRules.ruleSections[action.payload.sectionIndex];
                if (section?.conditionGroups) {
                    section.conditionGroups.splice(action.payload.groupIndex, 1);
                    state.hasUnsavedChanges = true;
                }
            }
        },
        addCondition: (state, action: PayloadAction<{ sectionIndex: number; groupIndex: number }>) => {
            if (state.updateRequest.modifierRules?.ruleSections) {
                const section = state.updateRequest.modifierRules.ruleSections[action.payload.sectionIndex];
                if (section?.conditionGroups) {
                    const group = section.conditionGroups[action.payload.groupIndex];
                    if (group) {
                        const newCondition: UpdateConditionRequestUI = {
                            id: undefined,
                            operator: ConditionOperator.Equals,
                            conditionField: ConditionField.Price,
                            comparedValues: undefined
                        };
                        if (!group.conditions) {
                            group.conditions = [];
                        }
                        group.conditions.push(newCondition);
                        state.hasUnsavedChanges = true;
                    }
                }
            }
        },
        updateCondition: (state, action: PayloadAction<{ sectionIndex: number; groupIndex: number; conditionIndex: number; condition: UpdateConditionRequestUI }>) => {
            if (state.updateRequest.modifierRules?.ruleSections) {
                const section = state.updateRequest.modifierRules.ruleSections[action.payload.sectionIndex];
                if (section?.conditionGroups) {
                    const group = section.conditionGroups[action.payload.groupIndex];
                    if (group?.conditions && group.conditions[action.payload.conditionIndex]) {
                        group.conditions[action.payload.conditionIndex] = action.payload.condition;
                        state.hasUnsavedChanges = true;
                    }
                }
            }
        },
        deleteCondition: (state, action: PayloadAction<{ sectionIndex: number; groupIndex: number; conditionIndex: number }>) => {
            if (state.updateRequest.modifierRules?.ruleSections) {
                const section = state.updateRequest.modifierRules.ruleSections[action.payload.sectionIndex];
                if (section?.conditionGroups) {
                    const group = section.conditionGroups[action.payload.groupIndex];
                    if (group?.conditions) {
                        group.conditions.splice(action.payload.conditionIndex, 1);
                        state.hasUnsavedChanges = true;
                    }
                }
            }
        },
        updateLocationQuoteeId: (state, action: PayloadAction<{ locationPublicId: string; quoteeId: string }>) => {
            const { locationPublicId, quoteeId } = action.payload;
            if (state.updateRequest.ivendiProviderConfig?.locationQuoteeIds) {
                const locationQuoteeIds = state.updateRequest.ivendiProviderConfig.locationQuoteeIds;
                const updatedIds = locationQuoteeIds.map(loc =>
                    loc.locationPublicId === locationPublicId
                        ? { ...loc, quoteeId }
                        : loc
                );
                state.updateRequest.ivendiProviderConfig.locationQuoteeIds = updatedIds;
                state.hasUnsavedChanges = true;
            }
        },
        resetToolSettings: (state) => {
            if (state.toolSettings) {
                state.updateRequest = mapToolSettingsResponseToRequest(
                    state.toolSettings,
                    state.updateRequest.botPublicId
                );
                state.hasUnsavedChanges = false;
            }
        }
    },
    extraReducers: (builder) => {
        builder.addCase(fetchToolSettingsById.fulfilled, (state, { payload }) => {
            state.toolSettings = payload;
            state.toolSettingsRequestStatus = RequestStatus.RESOLVED;
            state.updateRequest = mapToolSettingsResponseToRequest(payload, state.updateRequest.botPublicId);
            state.hasUnsavedChanges = false;
        });
        builder.addCase(fetchToolSettingsById.pending, (state) => {
            state.toolSettingsRequestStatus = RequestStatus.PENDING;
        });
        builder.addCase(fetchToolSettingsById.rejected, (state) => {
            state.toolSettingsRequestStatus = RequestStatus.REJECTED;
        });
        builder.addCase(updateToolSettings.pending, (state) => {
            state.updateSettingsStatus = RequestStatus.PENDING;
        });
        builder.addCase(updateToolSettings.fulfilled, (state, { payload }) => {
            state.updateSettingsStatus = RequestStatus.RESOLVED;
            state.toolSettings = payload;
            state.updateRequest = mapToolSettingsResponseToRequest(payload, state.updateRequest.botPublicId);
            state.hasUnsavedChanges = false;
        });
        builder.addCase(updateToolSettings.rejected, (state) => {
            state.updateSettingsStatus = RequestStatus.REJECTED;
        });
        builder.addCase(fetchFieldOptions.pending, (state) => {
            state.fieldOptionsStatus = RequestStatus.PENDING;
        });
        builder.addCase(fetchFieldOptions.fulfilled, (state, { payload }) => {
            state.fieldOptions = payload;
            state.fieldOptionsStatus = RequestStatus.RESOLVED;
        });
        builder.addCase(fetchFieldOptions.rejected, (state) => {
            state.fieldOptionsStatus = RequestStatus.REJECTED;
        });
    }
});

export const {
    updateSettings,
    addNewRetailPriceRule,
    updateRetailPriceRule,
    deleteRetailPriceRule,
    updateModifierRulesBasic,
    addRuleSection,
    updateRuleSection,
    deleteRuleSection,
    addConditionGroup,
    updateConditionGroup,
    deleteConditionGroup,
    addCondition,
    updateCondition,
    deleteCondition,
    updateLocationQuoteeId,
    resetToolSettings
} = toolSettingsSlice.actions;

export const toolSettingsSelector = (state: RootState): ToolSettingsResponse | undefined => state.toolSettings.toolSettings;
export const toolSettingsRequestStatusSelector = (state: RootState): RequestStatus => state.toolSettings.toolSettingsRequestStatus;
export const selectUpdateStatus = (state: RootState): RequestStatus => state.toolSettings.updateSettingsStatus;
export const selectHasUnsavedChanges = (state: RootState): boolean => state.toolSettings.hasUnsavedChanges;
export const selectRequestsConfiguration = (state: RootState): UpdateRequestsConfigurationRequest => state.toolSettings.updateRequest.requestsConfiguration;
export const selectBookingConfigs = (state: RootState): UpdateBookingConfigRequest[] => state.toolSettings.updateRequest.bookingConfigs;
export const selectCallbackBookingConfig = (state: RootState): UpdateBookingConfigRequest | undefined =>
    state.toolSettings.updateRequest.bookingConfigs.find(config => config.bookingType === BookingType.CallBack);
export const selectTestDriveBookingConfig = (state: RootState): UpdateBookingConfigRequest | undefined =>
    state.toolSettings.updateRequest.bookingConfigs.find(config => config.bookingType === BookingType.TestDrive);
export const selectRetailPriceRules = (state: RootState): UpdateRetailPriceRuleRequestPartial[] => state.toolSettings.updateRequest.retailPriceRules;
export const selectSearchNearestByPostcode = (state: RootState): boolean => state.toolSettings.updateRequest.searchNearestByPostcodeEnabled;
export const selectFinanceIddUrl = (state: RootState): string | undefined => state.toolSettings.updateRequest.financeIddUrl;
export const selectFinanceInterestRate = (state: RootState): number | undefined => state.toolSettings.updateRequest.financeInterestRate;
export const selectFinanceProvider = (state: RootState): FinanceProvider | undefined => state.toolSettings.updateRequest.financeProvider;
export const selectIvendiProviderConfig = (state: RootState): IVendiProviderConfigRequest | undefined => state.toolSettings.updateRequest.ivendiProviderConfig;
export const selectIvendiApiKey = (state: RootState): string | undefined => state.toolSettings.updateRequest.ivendiProviderConfig?.apiKey;
export const selectIvendiClientId = (state: RootState): string | undefined => state.toolSettings.updateRequest.ivendiProviderConfig?.clientId;
export const selectIvendiClientSecret = (state: RootState): string | undefined => state.toolSettings.updateRequest.ivendiProviderConfig?.clientSecret;
export const selectIsPcpEnabled = (state: RootState): boolean => state.toolSettings.updateRequest.isPcpEnabled || false;
export const selectPcpAPR = (state: RootState): number | undefined => state.toolSettings.updateRequest.pcpAPR;
export const selectPcpResidualPercentage = (state: RootState): number | undefined => state.toolSettings.updateRequest.pcpResidualPercentage;
export const selectPcpDealerContribution = (state: RootState): number | undefined => state.toolSettings.updateRequest.pcpDealerContribution;
export const selectValuationPriceType = (state: RootState): ValuationPriceType => state.toolSettings.updateRequest.valuationPriceType || ValuationPriceType.Retail;
export const selectValuationProvider = (state: RootState): ValuationProvider => state.toolSettings.updateRequest.valuationProvider || ValuationProvider.Autotrader;
export const selectValuationServiceGroupSoftwareHash = (state: RootState): string | undefined => state.toolSettings.updateRequest.valuationServiceGroupSoftwareHash;

export const selectStripePublicKey = (state: RootState): string | undefined => state.toolSettings.updateRequest.stripePublicKey;
export const selectStripeSecretKey = (state: RootState): string | undefined => state.toolSettings.updateRequest.stripeSecretKey;
export const selectCurrency = (state: RootState): Currency | undefined => state.toolSettings.updateRequest.currency;
export const selectPrice = (state: RootState): number | undefined => state.toolSettings.updateRequest.price;

export const selectMinDeduction = (state: RootState): number => state.toolSettings.updateRequest.minDeduction;
export const selectMaxDeduction = (state: RootState): number => state.toolSettings.updateRequest.maxDeduction;

export const selectConditionMultipliers = (state: RootState): UpdateConditionMultipliersRequest =>
    state.toolSettings.updateRequest.conditionMultipliers;
export const selectExcellentSubtraction = (state: RootState) =>
    state.toolSettings.updateRequest.conditionMultipliers.excellentSubtraction;
export const selectGoodSubtraction = (state: RootState) =>
    state.toolSettings.updateRequest.conditionMultipliers.goodSubtraction;
export const selectAverageSubtraction = (state: RootState) =>
    state.toolSettings.updateRequest.conditionMultipliers.averageSubtraction;
export const selectBelowAverageSubtraction = (state: RootState) =>
    state.toolSettings.updateRequest.conditionMultipliers.belowAverageSubtraction;
export const selectPoorSubtraction = (state: RootState) =>
    state.toolSettings.updateRequest.conditionMultipliers.poorSubtraction;

export const selectServiceHistoryMultipliers = (state: RootState): UpdateServiceHistoryMultipliersRequest =>
    state.toolSettings.updateRequest.serviceHistoryMultipliers;
export const selectFullHistorySubtraction = (state: RootState) =>
    state.toolSettings.updateRequest.serviceHistoryMultipliers.fullHistorySubtraction;
export const selectPartHistorySubtraction = (state: RootState) =>
    state.toolSettings.updateRequest.serviceHistoryMultipliers.partHistorySubtraction;
export const selectNoHistorySubtraction = (state: RootState) =>
    state.toolSettings.updateRequest.serviceHistoryMultipliers.noHistorySubtraction;

export const selectFieldOptions = (state: RootState): FieldOptionDTO[] => state.toolSettings.fieldOptions;
export const selectFieldOptionsStatus = (state: RootState): RequestStatus => state.toolSettings.fieldOptionsStatus;
export const selectModifierRules = (state: RootState): UpdateModifierRulesRequestUI | undefined => state.toolSettings.updateRequest.modifierRules;
export const selectModifierRulesIsActive = (state: RootState): boolean =>
    state.toolSettings.updateRequest.modifierRules?.isActive || false;
export const selectRuleSections = (state: RootState) =>
    state.toolSettings.updateRequest.modifierRules?.ruleSections || [];

export const selectConditionComparedValues = (sectionIndex: number, groupIndex: number, conditionIndex: number) =>
    (state: RootState): string[] | undefined => {
        const modifierRules = state.toolSettings.updateRequest.modifierRules;
        const section = modifierRules?.ruleSections?.[sectionIndex];
        const group = section?.conditionGroups?.[groupIndex];
        const condition = group?.conditions?.[conditionIndex];
        return condition?.comparedValues;
    };

export const selectConditionComparedValueAsNumber = (sectionIndex: number, groupIndex: number, conditionIndex: number) =>
    (state: RootState): number | undefined => {
        const values = selectConditionComparedValues(sectionIndex, groupIndex, conditionIndex)(state);
        return values && values.length > 0 ? parseFloat(values[0]) : undefined;
    };

export const selectLocationQuoteeId = (locationPublicId: string) => (state: RootState): string | undefined =>
    state.toolSettings.updateRequest.ivendiProviderConfig?.locationQuoteeIds?.find(
        loc => loc.locationPublicId === locationPublicId
    )?.quoteeId;

export default toolSettingsSlice.reducer;
